/**
 * 小票上的二维码。
 *
 * ★ 这一层只产出**矩阵**(每行一串 '0'/'1'),不产出图片、更不碰打印机。
 *
 *   为什么非这样不可:这张票有**四条**打印路径 —— 蓝牙(画成图发
 *   ESC/POS)、系统打印(排成 HTML)、电脑中转(收字节)、
 *   浏览器打印台(`apps/admin/public/station.html`,自己有一套渲染)。
 *   而打印台那一页是 ES5、零依赖、要在 Win7 上跑的,
 *   指望它去编码二维码是不可能的。
 *
 *   所以编码必须在**发出去之前**做完,发过去的是画起来不用动脑的东西:
 *   一格一格填黑。四条路各自怎么画随它们,但「画什么」只在这里决定一次。
 *
 * ★ 用现成的编码器,不自己写。二维码要做 Reed–Solomon 纠错、选版本、
 *   挑掩码 —— 自己写的话,错了的表现是「我这儿扫得出来,客户扫不出来」,
 *   而那是最难发现的一类错(没有任何报错,只有一张扫不动的纸)。
 */
import qrcode from 'qrcode-generator';

/*
 * ★ 把编码器的「字符串 → 字节」换成 UTF-8。
 *
 *   它自带的那个是 `c & 0xff` —— 每个字符只取低 8 位,也就是 latin-1。
 *   ASCII 的网址看不出问题,而「本店微信」里的每个汉字都会被截成
 *   一个不相干的字节:码印得出来、扫得动,**扫出来是乱码**。
 *   这是最坏的一种错 —— 没有任何报错,要等客户扫了才发现。
 *
 *   换在模块加载时做一次,不在每次调用里改:它是编码器上的全局状态,
 *   来回改的话取决于谁最后调过,而那种依赖顺序的 bug 查起来最费劲。
 */
qrcode.stringToBytes = (s: string): number[] => Array.from(new TextEncoder().encode(s));

/**
 * 二维码内容的两种来源,**二选一**。
 *
 * ★ 用一个 `mode` 判别,而不是「两个字段谁非空算谁」——
 *   后者一旦两个都填上,行为就取决于代码里谁先判断,
 *   而界面上看不出来哪个生效了。
 */
export type ReceiptQrConfig =
  /** 存一串字符(网址、微信收款码的内容…),打印时现生成 */
  | { mode: 'TEXT'; text: string }
  /**
   * 存一张图(商家自己那张码的截图)。
   *
   * ★ 存 `data:` URI,不存对象存储的 key:这张图要跟着打印任务一路走到
   *   收银机上那个打印台页面,那儿没有登录态、也拿不到预签名链接。
   *   内联之后四条路拿到的都是同一份字节,不依赖网络。
   *   代价是必须**限死大小**(见 MAX_IMAGE_BYTES)。
   */
  | { mode: 'IMAGE'; image: string };

/**
 * 字符串最长多少。
 *
 * ★ 不是技术上限(版本 40 能装两千多字节),是**印出来还扫得动**的上限。
 *   内容越长模块越密,而小票机在 28mm 见方里印一个 177×177 的码,
 *   一个模块不到一个点 —— 纸上是一团灰。
 *   卡在这儿,总比让人打出一叠扫不出来的票强。
 */
export const MAX_QR_TEXT = 512;

/**
 * 图片最大字节数(data: URI 整串算)。
 *
 * ★ 这个数是被**打印任务的 body 上限**倒推出来的,不是按「一张图该多大」定的。
 *
 *   这张图是内联在每一张小票里的,而后台打印一次最多发 50 张、
 *   服务端 body 上限是 2MB(apps/api/src/main.ts)。
 *   按 200KB 算,二十张票就是 5MB —— POST 当场被拒,
 *   而人看到的是「打印失败」,跟「那张二维码太大了」没有任何字面联系。
 *   这正是上一次浏览器打印台出事的形状:错在一头,现象在另一头。
 *
 *   64KB 下,50 张票的 base64 约 4.3MB —— 还是超。所以**另外**在发送端
 *   按体积分批(见 apps/admin/src/lib/printer.ts 的 postJobsInBatches)。
 *   两道一起上:这一道让单张不至于离谱,那一道保证多少张都发得出去。
 *
 * ★ 管理端上传时会先把图缩到 384px 再编码,正常的二维码截图缩完 3–10KB,
 *   离这个上限很远 —— 撞上限的多半是有人传了张相片,那时候该拦住他。
 */
export const MAX_QR_IMAGE_BYTES = 64 * 1024;

/**
 * 二维码印多大 —— 边长,单位是**打印点**(203dpi,8 点 = 1mm)。
 *
 * ★ 单位用点不用毫米:蓝牙那条路的画布就是按点算的,
 *   而 HTML 那两条除以 8 就是毫米 —— 从点换到毫米是整除,反过来不是。
 *
 * ★ 上限看纸:58mm 纸可打印 48mm(384 点),所以最大那档 360 点(45mm)
 *   在窄纸上也放得下。放不下的话打印机会把右边裁掉,
 *   而裁掉一角的二维码是扫不出来的 —— 那种失败没有任何提示。
 */
export const QR_DOTS: Record<string, number> = {
  off: 0,
  small: 128, // 16mm
  normal: 176, // 22mm
  big: 224, // 28mm
  huge: 288, // 36mm
  giant: 360, // 45mm
};

/**
 * 一段文字 → 二维码矩阵。每行一串 '0'/'1','1' 是黑。
 *
 * ★ 纠错等级选 M(约 15%)。热敏纸上印出来的码常常被蹭花、
 *   被胶带盖住一角,L(7%)在纸上不够用;而 Q/H 会让同样的内容
 *   需要更大的版本 —— 模块更密,在 28mm 见方里反而更难扫。
 *
 * ★ 版本传 0 = 让它按内容自己挑最小的那个。
 *
 * @returns 内容为空或编不出来时返回 null —— 让调用方决定「不印」,
 *   而不是在这儿抛异常把整张票带崩:少一个二维码是小事,
 *   一叠票打不出来是大事。
 */
export function qrModules(text: string): string[] | null {
  const content = text.trim();
  if (!content || content.length > MAX_QR_TEXT) return null;
  try {
    const qr = qrcode(0, 'M');
    qr.addData(content, 'Byte');
    qr.make();
    const n = qr.getModuleCount();
    const rows: string[] = [];
    for (let r = 0; r < n; r++) {
      let row = '';
      for (let c = 0; c < n; c++) row += qr.isDark(r, c) ? '1' : '0';
      rows.push(row);
    }
    return rows;
  } catch {
    return null;
  }
}
